/**
 * The sign-in form, in an editor window.
 *
 * Praxadon is a place where children write HTML, so its front door is the thing they
 * are about to use: a window with a tab, a gutter of line numbers, and a status bar.
 * The chrome is scenery — hidden from screen readers, unselectable, never in the tab
 * order — and everything that matters underneath it is an ordinary, labelled form.
 *
 * The rule the whole design bends to: a password field must be easy to read. So the
 * window is a solid surface, the fields are the product's normal fields, and the code
 * flourishes stay in the margins where they cannot get in the way.
 */

import { cn } from '@praxadon/ui';
import { type ReactNode } from 'react';

/** Enough numbers for the tallest of these pages; the window clips the rest. */
const GUTTER = Array.from({ length: 60 }, (_, index) => index + 1);

/** The little lights, for the look of a window. */
function Lights() {
  return (
    <span aria-hidden className="flex items-center gap-1.5 pl-1">
      <span className="size-3 rounded-full bg-[#ff5f57]" />
      <span className="size-3 rounded-full bg-[#febc2e]" />
      <span className="size-3 rounded-full bg-[#28c840]" />
    </span>
  );
}

export interface EditorFrameProps {
  /** The name on the tab: `sign-in.html`, `platform.sh`. */
  file: string;
  /** What the status bar says on the right: the language, usually. */
  language?: string;
  children: ReactNode;
  className?: string;
}

export function EditorFrame({ file, language = 'HTML', children, className }: EditorFrameProps) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-surface-raised/95 shadow-2xl shadow-black/50 backdrop-blur-xl',
        className,
      )}
    >
      {/* Title bar: the lights, and the file we are pretending to have open. */}
      <div className="flex items-center gap-3 border-b border-border bg-surface-sunken/80 px-3 py-2">
        <Lights />
        <span
          aria-hidden
          className="ml-1 flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1 font-mono text-xs text-fg-muted"
        >
          <span className="size-1.5 rounded-full bg-accent" />
          {file}
        </span>
      </div>

      <div className="flex">
        {/*
          The gutter. Numbers, because an editor has numbers — more of them than any of
          these pages is tall, clipped by the window, so it never stops short of the
          bottom on a long form or run past it on a short one.
        */}
        <div
          aria-hidden
          className="relative hidden w-11 shrink-0 overflow-hidden border-r border-border bg-surface-sunken/50 select-none sm:block"
          style={{
            // The window cuts the numbers off wherever it ends, which would slice one in
            // half. Fading the last row makes the ending look deliberate, because it is.
            maskImage: 'linear-gradient(to bottom, black calc(100% - 1.75rem), transparent)',
            WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 1.75rem), transparent)',
          }}
        >
          <div className="absolute inset-0 px-2.5 py-5 text-right font-mono text-xs leading-7 text-fg-subtle/60">
            {GUTTER.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </div>

        <div className="min-w-0 flex-1 p-5 sm:p-6">{children}</div>
      </div>

      {/* Status bar, exactly as useless and as reassuring as the real one. */}
      <div
        aria-hidden
        className="flex items-center justify-between gap-3 border-t border-border bg-surface-sunken/80 px-3 py-1.5 font-mono text-[11px] text-fg-subtle"
      >
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-success" />
          ready
        </span>
        <span className="flex items-center gap-3">
          <span className="hidden sm:inline">UTF-8</span>
          <span>{language}</span>
        </span>
      </div>
    </div>
  );
}

/**
 * A line of code around the form — an opening tag, a closing one, a comment.
 *
 * `<!--` and `-->` are punctuation for the eye, not for the ear: they are hidden from
 * screen readers, which hear the words between them and nothing else.
 */
export function CodeLine({
  open,
  close,
  tone = 'tag',
  children,
  className,
}: {
  open?: string;
  close?: string;
  tone?: 'tag' | 'comment';
  children?: ReactNode;
  className?: string;
}) {
  const colour = tone === 'comment' ? 'text-fg-subtle' : 'text-accent-text';
  return (
    <p className={cn('no-ligatures font-mono text-sm', colour, className)}>
      {open ? (
        <span aria-hidden className="opacity-70">
          {open}
        </span>
      ) : null}
      {children}
      {close ? (
        <span aria-hidden className="opacity-70">
          {close}
        </span>
      ) : null}
    </p>
  );
}

/** A terminal caret that blinks — and stops blinking when the reader asked it to. */
export function Caret({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block h-4 w-[7px] translate-y-[2px] bg-accent caret-blink', className)}
    />
  );
}
